import { useState, useEffect, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog'
import { Button } from '../ui/button'
import { Loader2, Crown, Sparkles, TrendingUp, CheckCircle, XCircle, ExternalLink } from 'lucide-react'
import { apiClient } from '../../lib/api'
import type { MembershipTierConfig, MembershipTier } from '../../types'
import { MembershipTierCard } from './MembershipTierCard'
import { MembershipBenefitsList } from './MembershipBenefitsList'

interface MembershipUpgradeDialogProps {
  currentTier: MembershipTier
  onClose: () => void
  onUpgraded: () => void
}

interface UpgradeResponse {
  success: boolean
  membership?: any
  subscription_id?: string
  checkout_url?: string
  session_id?: string
  status?: string
  message?: string
  error?: string
  requires_payment?: boolean
  tier?: string
}

type PaymentStatus = 'idle' | 'selecting' | 'waiting' | 'completed' | 'failed';

const POLL_INTERVAL = 2000; // 2 seconds
const MAX_POLL_TIME = 10 * 60 * 1000; // 10 minutes max wait

export function MembershipUpgradeDialog({ currentTier, onClose, onUpgraded }: MembershipUpgradeDialogProps) {
  const { t } = useTranslation();
  const [tiers, setTiers] = useState<MembershipTierConfig[]>([])
  const [loading, setLoading] = useState(true)
  const [upgrading, setUpgrading] = useState(false)
  const [selectedTier, setSelectedTier] = useState<MembershipTierConfig | null>(null)
  
  // Payment state
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>('idle')
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null)
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const pollStartTimeRef = useRef<number>(0)
  const checkoutWindowRef = useRef<Window | null>(null)

  useEffect(() => {
    loadTiers()
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
      }
    }
  }, [])

  const loadTiers = async () => {
    try {
      const response = await apiClient.membership.getMembershipTiers()
      if (response.success) {
        setTiers(response.tiers)
      }
    } catch (error) {
      console.error('Error loading tiers:', error)
    } finally {
      setLoading(false)
    }
  }

  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
    }
  }

  const startPolling = useCallback((tier: string) => {
    pollStartTimeRef.current = Date.now()
    
    pollIntervalRef.current = setInterval(async () => {
      // Check if we've been polling too long
      if (Date.now() - pollStartTimeRef.current > MAX_POLL_TIME) {
        stopPolling()
        setPaymentStatus('failed')
        return
      }
      
      try {
        const statusResult = await apiClient.membership.getUpgradeStatus(tier)
        
        if (statusResult.status === 'completed') {
          stopPolling()
          setPaymentStatus('completed')
          
          // Close checkout tab if still open
          if (checkoutWindowRef.current && !checkoutWindowRef.current.closed) {
            checkoutWindowRef.current.close()
          }
        } else if (statusResult.status === 'failed') {
          stopPolling()
          setPaymentStatus('failed')
        }
        // If still 'pending', continue polling
      } catch (error) {
        console.error('Error checking upgrade status:', error)
        // Continue polling on error
      }
    }, POLL_INTERVAL)
  }, [])

  const handleUpgrade = async (tier: MembershipTier) => {
    try {
      setUpgrading(true)
      const tierConfig = tiers.find(t => t.tier_id === tier)
      setSelectedTier(tierConfig || null)
      
      const response = await apiClient.membership.upgradeMembership(tier) as UpgradeResponse
      
      if (response.success) {
        // Check if payment is required (checkout_url returned)
        if (response.checkout_url) {
          // Open Stripe Checkout in new tab
          setCheckoutUrl(response.checkout_url)
          checkoutWindowRef.current = window.open(response.checkout_url, '_blank')
          setPaymentStatus('waiting')
          
          // Start polling for upgrade completion
          startPolling(tier)
          
          setUpgrading(false)
          return
        }
        
        // No payment required (free tier or already paid)
        onUpgraded()
        onClose()
      } else {
        alert(response.error || response.message || 'Failed to upgrade membership')
      }
    } catch (error: any) {
      console.error('Error upgrading membership:', error)
      alert(error.message || 'Failed to upgrade membership')
    } finally {
      setUpgrading(false)
    }
  }

  const handleClose = () => {
    stopPolling()
    
    if (paymentStatus === 'completed') {
      onUpgraded()
    }
    
    // Close checkout tab if still open
    if (checkoutWindowRef.current && !checkoutWindowRef.current.closed) {
      checkoutWindowRef.current.close()
    }
    
    onClose()
  }

  const handleOpenCheckout = () => {
    if (checkoutUrl) {
      checkoutWindowRef.current = window.open(checkoutUrl, '_blank')
    }
  }

  const handleCancel = () => {
    stopPolling()
    setPaymentStatus('idle')
    setCheckoutUrl(null)
    setSelectedTier(null)
    
    // Close checkout tab if still open
    if (checkoutWindowRef.current && !checkoutWindowRef.current.closed) {
      checkoutWindowRef.current.close()
    }
  }

  const getTierIcon = (tierId: string) => {
    switch (tierId) {
      case 'book_giant':
        return <Crown className="h-6 w-6 text-yellow-500" />
      case 'plus':
        return <Sparkles className="h-6 w-6 text-purple-500" />
      default:
        return <TrendingUp className="h-6 w-6 text-blue-500" />
    }
  }

  // Show payment waiting/completion dialog
  if (paymentStatus === 'waiting' || paymentStatus === 'completed' || paymentStatus === 'failed') {
    return (
      <Dialog open={true} onOpenChange={() => paymentStatus !== 'waiting' && handleClose()}>
        <DialogContent className="payment-status-dialog sm:max-w-md" onPointerDownOutside={(e) => paymentStatus === 'waiting' && e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {paymentStatus === 'completed' ? (
                <CheckCircle className="h-5 w-5 text-green-600" />
              ) : paymentStatus === 'failed' ? (
                <XCircle className="h-5 w-5 text-destructive" />
              ) : (
                <Loader2 className="h-5 w-5 animate-spin" />
              )}
              {paymentStatus === 'completed' 
                ? t('payment.membershipActivated', 'Membership Activated!')
                : paymentStatus === 'failed'
                ? t('payment.paymentFailed', 'Payment Failed')
                : t('membership.upgrading', 'Upgrading Membership')
              }
            </DialogTitle>
            <DialogDescription>
              {paymentStatus === 'waiting' 
                ? t('payment.completeInNewTab', 'Complete your payment in the new tab. This dialog will update automatically.')
                : paymentStatus === 'completed'
                ? t('payment.membershipNowActive', 'Your membership is now active.')
                : t('payment.paymentDidNotComplete', 'The payment did not complete. Please try again.')
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Selected Tier Info */}
            {selectedTier && paymentStatus !== 'completed' && (
              <div className="bg-muted/50 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  {getTierIcon(selectedTier.tier_id)}
                  <div>
                    <p className="font-semibold">{selectedTier.name}</p>
                    <p className="text-sm text-muted-foreground">
                      ${selectedTier.price_monthly_usd}/month
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Status Display */}
            {paymentStatus === 'waiting' && (
              <div className="flex flex-col items-center justify-center py-8 space-y-4">
                <Loader2 className="h-12 w-12 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground text-center">
                  {t('payment.waitingForPayment', 'Waiting for payment confirmation...')}
                </p>
                <p className="text-xs text-muted-foreground text-center">
                  {t('payment.dontCloseDialog', "Don't close this dialog until payment is complete.")}
                </p>
              </div>
            )}

            {paymentStatus === 'completed' && selectedTier && (
              <div className="flex flex-col items-center justify-center py-6 space-y-4">
                <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <CheckCircle className="h-10 w-10 text-green-600" />
                </div>
                <div className="text-center">
                  <p className="text-lg font-semibold">
                    {selectedTier.name} {t('membership.activated', 'Activated')}
                  </p>
                  <p className="text-sm text-muted-foreground mb-4">
                    {t('membership.yourNewBenefits', 'Your new benefits:')}
                  </p>
                </div>
                {/* Benefits list from tier config */}
                <div className="w-full bg-muted/30 rounded-lg p-4">
                  <MembershipBenefitsList tier={selectedTier} />
                </div>
              </div>
            )}

            {paymentStatus === 'failed' && (
              <div className="flex flex-col items-center justify-center py-8 space-y-4">
                <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
                  <XCircle className="h-10 w-10 text-destructive" />
                </div>
                <p className="text-sm text-muted-foreground text-center">
                  {t('payment.tryAgainOrContact', 'Please try again or contact support if the problem persists.')}
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="pt-2 space-y-2">
              {paymentStatus === 'waiting' && (
                <>
                  <Button
                    onClick={handleOpenCheckout}
                    variant="outline"
                    className="w-full"
                  >
                    <ExternalLink className="mr-2 h-4 w-4" />
                    {t('payment.reopenPaymentPage', 'Reopen Payment Page')}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleCancel}
                    className="w-full"
                  >
                    {t('common.cancel', 'Cancel')}
                  </Button>
                </>
              )}
              
              {paymentStatus === 'completed' && (
                <Button onClick={handleClose} className="w-full">
                  {t('common.continue', 'Continue')}
                </Button>
              )}
              
              {paymentStatus === 'failed' && (
                <>
                  <Button onClick={handleOpenCheckout} className="w-full">
                    {t('payment.tryAgain', 'Try Again')}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCancel}
                    className="w-full"
                  >
                    {t('common.cancel', 'Cancel')}
                  </Button>
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  // Show tier selection dialog
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="upgrade-dialog max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('membership.upgradeTitle', 'Upgrade Your Membership')}</DialogTitle>
          <DialogDescription>
            {t('membership.chooseAPlan', 'Choose a plan that fits your needs')}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            {tiers.map((tier) => {
              const isCurrentTier = tier.tier_id === currentTier

              return (
                <MembershipTierCard
                  key={tier.tier_id}
                  tier={tier}
                  isCurrentTier={isCurrentTier}
                  canUpgrade={!isCurrentTier}
                  upgrading={upgrading}
                  onUpgrade={handleUpgrade}
                  variant="full"
                />
              )
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
